import { FormSection, styled, useTheme } from "@monolith-forensics/monolith-ui";
import { ExternalLinkIcon } from "lucide-react";
import { useFeatures } from "@/contexts/FeaturesContext";
import { CUSTOM_FIELD_CATEGORIES } from "@/Features";
import CustomFieldsTemplate from "./CustomFieldsTemplate";
import customFieldAPIBundler from "./utils/customFieldAPIBundler";

const DOCS_URL =
  "https://docs.monolithforensics.com/monolith/monolith-features/settings/custom-fields";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  overflow-y: auto;
  max-width: 800px;
  padding: 10px 30px;
  gap: 20px;
`;

const Header = styled.h2`
  margin: 0 0 8px 0;
  font-weight: 600;
`;

const Description = styled.p`
  margin: 0 0 6px 0;
  color: ${({ theme }) => theme.palette.text.secondary};
  line-height: 1.6;
  font-size: 14px;
`;

const DocsLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.palette.primary.main};
  text-decoration: none;
  font-size: 14px;
  margin-top: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const CustomFieldSettings = () => {
  const theme = useTheme();
  const { hasFeatures, FEATURES } = useFeatures();
  const hasLabAssets = hasFeatures([FEATURES.LAB_ASSETS]);
  const categories = customFieldAPIBundler.filter(
    (api) => api.id !== CUSTOM_FIELD_CATEGORIES.ASSET || hasLabAssets
  );

  return (
    <Container>
      <div>
        <Header>Custom Fields</Header>
        <Description>
          Custom fields allow you to extend Monolith by adding your own data
          fields to Cases, Evidence, Acquisitions, Inquiries,{" "}
          {hasLabAssets ? "Storage, and Assets" : "and Storage"}. You can
          configure each field as a text box, date picker, drop down menu, or
          tag box, and choose whether it should be required. Once created, a
          field's editor type cannot be changed, but fields can still be
          enabled, disabled, reordered, edited, or deleted at any time.
        </Description>
        <DocsLink href={DOCS_URL} target="_blank" rel="noopener noreferrer">
          Learn more in the docs
          <ExternalLinkIcon size={14} color={theme.palette.primary.main} />
        </DocsLink>
      </div>
      {categories.map((api) => {
        return (
          <FormSection
            key={api.id}
            title={`Custom ${api.id} Fields`}
            defaultOpen={false}
            size="lg"
            style={{ textTransform: "capitalize" }}
          >
            <CustomFieldsTemplate
              category={api.id}
              description={api.description}
              handleCreateField={api.createField}
              handleUpdateField={api.updateField}
              handleDeleteField={api.deleteField}
              getFields={api.getFields}
              getTypes={api.getTypes}
            />
          </FormSection>
        );
      })}
    </Container>
  );
};

export default CustomFieldSettings;
